import { ItemTypes } from '../constants/constants';
import {
  getSectionRecursiveMeta,
  allowedChildModels,
  deriveAgreementType,
} from './recursiveSections';

// Construction de l'arbre des sections imbriquées affiché dans le panel gauche
// (onglet « Sections »). Deux modes :
//  - PROPOSITION : sections déjà bakées → on lit la chaîne d'ancêtres `sectionPath`
//    persistée sur les containers/box (cf. utils/sections.js mutateInjectSection).
//    Les ancres SECTION_LINK sont retirées après injection, d'où la nécessité du
//    chemin persisté plutôt qu'un chaînage par `fromBoxId`.
//  - TEMPLATE : sections NON bakées → descente récursive via getTemplate.
// Tout est défensif : une entrée non rattachable devient une racine, jamais d'exception.

const ANCHOR_TYPES = new Set([
  ItemTypes.SECTION_LINK,
  ItemTypes.SECTION_VARIABLE,
  ItemTypes.RECURSIVE_SECTION,
  ItemTypes.RECURSIVE_MODEL,
]);

const MAX_DEPTH = 20;

const sectionLabel = (section, meta, recursiveModelLabels) => {
  if (section?.name) return section.name;
  if (meta?.recursiveModelName) {
    return recursiveModelLabels?.[meta.recursiveModelName] ?? meta.recursiveModelName;
  }
  return 'Section';
};

// ---------- Mode PROPOSITION (sync, depuis le contenu baké) ----------

export function buildProposalSectionTree({
  boxes = [],
  containers = [],
  sections = [],
  recursiveModelLabels = {},
} = {}) {
  const sectionsById = new Map(sections.map((s) => [String(s.id), s]));

  // box lookup par columnId pour retrouver une box d'ancrage (scroll canvas).
  const firstBoxByColumnId = new Map();
  for (const b of boxes) {
    if (b?.columnId != null && !firstBoxByColumnId.has(b.columnId)) {
      firstBoxByColumnId.set(b.columnId, b);
    }
  }

  // Regrouper les containers bakés par chemin de section (pathKey).
  const groups = new Map(); // pathKey -> { path, tipSectionId, timestamps:Set, anchorBox, order }
  containers.forEach((c, idx) => {
    const path = Array.isArray(c?.sectionPath) ? c.sectionPath : null;
    if (!path || path.length === 0) return;
    const pathKey = path.map(String).join('>');
    let g = groups.get(pathKey);
    if (!g) {
      g = {
        path: path.map(String),
        tipSectionId: String(path[path.length - 1]),
        timestamps: new Set(),
        anchorBox: null,
        order: idx,
      };
      groups.set(pathKey, g);
    }
    if (c.dateTimestamp != null) g.timestamps.add(c.dateTimestamp);
    if (!g.anchorBox && Array.isArray(c.columnsIds)) {
      for (const colId of c.columnsIds) {
        const box = firstBoxByColumnId.get(colId);
        if (box) {
          g.anchorBox = box;
          break;
        }
      }
    }
  });

  if (groups.size === 0) return [];

  // Une section dont tout le contenu est fait de sous-sections n'a aucun
  // container à son propre chemin : sans ce passage elle n'avait pas de nœud,
  // et ses sous-sections remontaient à la racine. On crée ses ancêtres, ancrés
  // sur leur premier descendant.
  for (const g of [...groups.values()]) {
    for (let cut = 1; cut < g.path.length; cut++) {
      const prefix = g.path.slice(0, cut);
      const prefixKey = prefix.join('>');
      const existing = groups.get(prefixKey);
      if (!existing) {
        groups.set(prefixKey, {
          path: prefix,
          tipSectionId: prefix[prefix.length - 1],
          timestamps: new Set(),
          anchorBox: g.anchorBox,
          order: g.order,
          synthetic: true,
        });
      } else if (existing.synthetic && g.order < existing.order) {
        existing.order = g.order;
        existing.anchorBox = g.anchorBox;
      }
    }
  }

  // Créer un nœud par pathKey.
  const nodeByKey = new Map();
  for (const [pathKey, g] of groups) {
    const section = sectionsById.get(g.tipSectionId);
    const meta = getSectionRecursiveMeta(section);
    const count = g.timestamps.size || 1;
    nodeByKey.set(pathKey, {
      key: pathKey,
      label: sectionLabel(section, meta, recursiveModelLabels),
      kind: meta.isRecursive ? 'recursive' : 'linked',
      modelName: meta.recursiveModelName ?? null,
      sectionId: g.tipSectionId,
      anchorBoxId: g.anchorBox?.id ?? null,
      count: meta.isRecursive ? count : 1,
      order: g.order,
      children: [],
    });
  }

  // Rattacher chaque nœud au plus long préfixe existant (parent). Sinon racine.
  const roots = [];
  for (const [pathKey, node] of nodeByKey) {
    const path = groups.get(pathKey).path;
    let parentNode = null;
    for (let cut = path.length - 1; cut >= 1; cut--) {
      const candidate = path.slice(0, cut).join('>');
      if (nodeByKey.has(candidate)) {
        parentNode = nodeByKey.get(candidate);
        break;
      }
    }
    if (parentNode) parentNode.children.push(node);
    else roots.push(node);
  }

  const sortRec = (list) => {
    list.sort((a, b) => a.order - b.order);
    list.forEach((n) => sortRec(n.children));
  };
  sortRec(roots);
  return roots;
}

// ---------- Mode TEMPLATE (async, descente via getTemplate) ----------

export async function buildTemplateSectionTree({
  boxes = [],
  sections = [],
  currentTemplate = null,
  recursiveSectionRelationships = {},
  recursiveModelLabels = {},
  getTemplate,
} = {}) {
  const sectionsById = new Map(sections.map((s) => [String(s.id), s]));

  // Ancres racine = SECTION_LINK de premier niveau posées dans le doc courant.
  // Exclure les ancres bakées (fromBoxId défini) : ce sont des enfants issus de
  // l'expansion d'une autre section, découverts via la descente getTemplate, pas
  // des racines (sinon un enfant apparaît aussi en doublon à la racine).
  const rootLinkBoxes = boxes.filter(
    (b) => b?.type === ItemTypes.SECTION_LINK && b.sectionId && !b.fromBoxId
  );
  const seenRootIds = new Set();
  const rootRefs = [];
  for (const b of rootLinkBoxes) {
    const sid = String(b.sectionId);
    if (seenRootIds.has(sid)) continue;
    seenRootIds.add(sid);
    rootRefs.push({ sectionId: b.sectionId, anchorBoxId: b.id });
  }

  const buildNode = async (sectionId, anchorBoxId, keyPrefix, visited, depth) => {
    const sid = String(sectionId);
    if (depth > MAX_DEPTH || visited.has(sid)) return null;
    const nextVisited = new Set(visited).add(sid);

    let section = sectionsById.get(sid);
    let content = section?.content;
    // Le contenu détaillé n'est pas toujours dans state.sections → fetch (caché).
    if (!content && typeof getTemplate === 'function') {
      try {
        const fetched = await getTemplate(sectionId);
        if (fetched && !fetched.error) {
          section = { ...(section || {}), ...fetched };
          content = fetched.content;
        }
      } catch (e) {
        // section introuvable / fetch échoué → nœud feuille sans enfants.
      }
    }
    const meta = getSectionRecursiveMeta(section);
    const node = {
      key: `${keyPrefix}/${sid}`,
      label: sectionLabel(section, meta, recursiveModelLabels),
      kind: meta.isRecursive ? 'recursive' : 'linked',
      modelName: meta.recursiveModelName ?? null,
      sectionId: sid,
      anchorBoxId: anchorBoxId ?? null,
      count: 1,
      children: [],
    };

    const childBoxes = Array.isArray(content?.boxes) ? content.boxes : [];
    // Enfants concrets : SECTION_LINK imbriqués + placeholders RECURSIVE_SECTION.
    for (const cb of childBoxes) {
      let childId = null;
      if (cb?.type === ItemTypes.SECTION_LINK && cb.sectionId) childId = cb.sectionId;
      else if (cb?.type === ItemTypes.RECURSIVE_SECTION && cb.content?.childTemplateId) {
        childId = cb.content.childTemplateId;
      }
      if (childId == null) continue;
      const childNode = await buildNode(childId, null, node.key, nextVisited, depth + 1);
      if (childNode) node.children.push(childNode);
    }

    // Enfants « possibles » (règles récursives) si section récursive sans enfant concret.
    if (meta.isRecursive && meta.recursiveModelName && node.children.length === 0) {
      const allowed = allowedChildModels(
        recursiveSectionRelationships,
        deriveAgreementType(section || currentTemplate),
        meta.recursiveModelName
      );
      allowed.forEach((childModel) => {
        node.children.push({
          key: `${node.key}/possible-${childModel}`,
          label: recursiveModelLabels?.[childModel] ?? childModel,
          kind: 'possible',
          modelName: childModel,
          sectionId: null,
          anchorBoxId: null,
          count: 1,
          children: [],
        });
      });
    }

    return node;
  };

  const roots = [];
  for (const ref of rootRefs) {
    const node = await buildNode(ref.sectionId, ref.anchorBoxId, 'root', new Set(), 0);
    if (node) roots.push(node);
  }
  return roots;
}
